import React, { useSyncExternalStore } from 'react';
import BaseAvatar from '@mui/material/Avatar';
import clsx from 'clsx';
import type { SxProps, Theme } from '@mui/material/styles';
import { SizeVariant } from '../../types/variants';

export type AvatarSize = SizeVariant | 'xsmall' | 'xlarge';

const SIZE_SX: Record<AvatarSize, SxProps<Theme>> = {
  xsmall: { width: 24, height: 24, fontSize: '1rem' },
  small: { width: 32, height: 32, fontSize: '1.25rem' },
  medium: { width: 48, height: 48, fontSize: '1.5rem' },
  large: { width: 64, height: 64, fontSize: '2rem' },
  xlarge: { width: 120, height: 120, fontSize: '2.5rem' },
};

const SIZE_STYLE: Record<AvatarSize, React.CSSProperties> = {
  xsmall: { width: 24, height: 24, fontSize: 16 },
  small: { width: 32, height: 32, fontSize: 20 },
  medium: { width: 48, height: 48, fontSize: 24 },
  large: { width: 64, height: 64, fontSize: 32 },
  xlarge: { width: 120, height: 120, fontSize: 40 },
};

const AVATAR_TEXT_SX: SxProps<Theme> = {
  fontWeight: 700,
  fontFamily: 'Open Sans, sans-serif',
};

const PLACEHOLDER_TEXT_STYLE: React.CSSProperties = {
  fontWeight: 700,
  fontFamily: 'Open Sans, sans-serif',
};

const emptySubscribe = () => () => {};
const useHasMounted = () =>
  useSyncExternalStore(
    emptySubscribe,
    () => true,
    () => false,
  );

/**
 * SSR + first client paint use inline styles so Emotion class hashes cannot diverge during hydration.
 * MUI Avatar renders after mount (Next.js Pages Router + Emotion cache mismatch).
 */
const Avatar = (props: AvatarPropTypes) => {
  const { username, src, srcSet, alt, size = 'medium', className, sx, ...rest } = props;
  const mounted = useHasMounted();

  const displayContent = username && username.trim() ? username[0].toUpperCase() : 'U';
  const ariaLabel =
    username && username.trim() ? `Avatar for user ${username}` : 'Default user avatar';

  if (!mounted) {
    return (
      <span
        className={clsx(
          'MuiAvatar-root',
          'MuiAvatar-rounded',
          'MuiAvatar-colorDefault',
          className,
        )}
        role="img"
        aria-label={ariaLabel}
        style={{
          ...SIZE_STYLE[size],
          ...PLACEHOLDER_TEXT_STYLE,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          flexShrink: 0,
          lineHeight: 1,
        }}
      >
        {src ? (
          <img
            src={src}
            srcSet={srcSet}
            alt={alt ?? ''}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          displayContent
        )}
      </span>
    );
  }

  return (
    <BaseAvatar
      className={className}
      variant="rounded"
      aria-label={ariaLabel}
      src={src}
      srcSet={srcSet}
      alt={alt}
      sx={[AVATAR_TEXT_SX, SIZE_SX[size], ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
      {...rest}
    >
      {displayContent}
    </BaseAvatar>
  );
};

export type AvatarPropTypes = {
  username: string;
  size?: AvatarSize;
  src?: string;
  srcSet?: string;
  alt?: string;
  className?: string;
  sx?: SxProps<Theme>;
} & Omit<React.ComponentProps<typeof BaseAvatar>, 'sx'>;

export default Avatar;
